import * as React from "react";
import styled, { css } from "styled-components";
import { useDispatch } from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { InstalledModuleType } from "src/utils/constants";
import OrderLineError from "src/core/orders/components/OrderLineError";
import Price from "src/core/prices/components/Price";
import ProductQuantity from "src/core/products/components/ProductQuantity";
import { shouldShowOrderLineErrors } from "src/core/orders/utils/orders";
import { useCanOrderProduct } from "src/core/orders/hooks/validation";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { IDrop } from "src/core/drops/api/drops.api";
import { IOrder } from "src/core/orders/api/orders.api";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { ProductStatus } from "src/core/products/api/products.api";
import { useIsMobile } from "src/library/hooks";
import { useQuickViewProduct } from "src/core/products/components/QuickViewProduct";
import { ProductQuantityControls } from "src/core/products/components/ProductDetails/ProductQuantityControls";
import { useProductIsAllowedSubs } from "src/core/products/hooks/useProductIsAllowedSubs";
import useProductIsOrderable from "src/core/products/hooks/useProductIsOrderable";

import { ProductUnavailableTag } from "../../products/components/ProductUnavailableTag";
import { PartNumber } from "../../products/PartNumber";
import {
  IOrderLine,
  OrderMode
} from "../api/orders.api";
import { updateOrderLine } from "../store/orders.actions";

interface IOrderProductLine {
  orderLine: IOrderLine;
  drop: IDrop;
  order?: IOrder;
  allowedDays: number | null;
  orderMode: OrderMode;
  installedModules: InstalledModuleType[];
}

const OrderProductLine: React.ReactFCWithChildren<IOrderProductLine> = ({
  orderLine,
  drop,
  order,
  orderMode,
  installedModules
}) => {
  const pricesVisible = usePriceVisibility();
  const canOrderProduct = useCanOrderProduct(orderLine.product, order?.orderDeliveryDate || null, orderLine);
  const isMobile = useIsMobile();
  const { setQuickViewProduct } = useQuickViewProduct();
  const dispatch = useDispatch();
  const allowedSubs = useProductIsAllowedSubs(orderLine.product);
  const unavailable = allowedSubs && orderLine.productSubstitution?.message || null;
  const { orderable } = useProductIsOrderable(orderLine.product, unavailable);

  const orderLineHasError = React.useCallback(() => {
    if (order && drop && shouldShowOrderLineErrors(order, orderMode, drop.groupedSettings)) {
      if (orderLine.errors && orderLine.errors.length > 0) {
        return true;
      } else if (orderLine.product.currentStatus?.status === ProductStatus.Suspended || orderLine.product.currentStatus?.status === ProductStatus.Delisted) {
        return true;
      }

      if (drop.groupedSettings.delivery_week_leadtimes && drop.groupedSettings.delivery_cutoff) {
        if (!canOrderProduct) {
          return true;
        }
      }
    }

    return false;
  }, [
    orderLine,
    order,
    drop,
    orderMode,
    canOrderProduct
  ]);

  const canRemove = React.useMemo(() => {
    // always allow remove on draft
    if (order?.orderStatus?.settings.group === "DRAFT") {
      return true;
    }

    return !orderLineHasError();
  }, [ order?.orderStatus?.settings.group, orderLineHasError ]);

  const lineMessage = React.useMemo(() => {
    let lineMessage = "";
    const productCurrentStatus = orderLine.product.currentStatus?.status;

    if (productCurrentStatus === ProductStatus.Suspended || productCurrentStatus === ProductStatus.Delisted) {
      lineMessage = `This product has been ${productCurrentStatus}`;
    } else if (orderLine.productSubstitution) {
      lineMessage = orderLine.productSubstitution.message;
    } else if (!canOrderProduct && orderMode === "edit") {
      lineMessage = "This product is not available to order right now";
    }

    return lineMessage;
  }, [
    orderLine,
    orderMode,
    canOrderProduct
  ]);

  return (
    <>
      <OrderLineWrapper
        direction="horizontal"
        valign="center"
        className={`${orderLineHasError() ? "error" : lineMessage ? "disabled" : "editable"}`}
        // styled-components transient prop (not forwarded to DOM)
        $substitution={!!orderLine.substituteOrderLine}
      >
        {order && drop && (
          <>
            <Grid.Box className="image-wrapper">
              <BrandedImage
                s3Key={orderLine.product && orderLine.product.image}
                alt={orderLine.product && orderLine.product.description}
                level="public"
              />
            </Grid.Box>

            <Grid.Bounds
              className="product-details"
              direction="vertical"
            >
              <div
                onClick={() => setQuickViewProduct(orderLine.productId)}
                className="link-text"
              >
                <TextTruncate
                  as="h3"
                  fontSize="h3"
                  truncateProps={{ lines: isMobile ? 1 : 2 }}
                >
                  {orderLine.product.description}
                </TextTruncate>
              </div>

              <PartNumber product={orderLine.product} />

              {drop && pricesVisible && (
                <>
                  <Price
                    mode={orderMode}
                    line={orderLine}
                    product={orderLine.product ? orderLine.product : null}
                    installedModules={installedModules}
                    quantity={orderLine.quantity}
                    type="unit"
                    usePackSizeMultiplier={orderMode === "edit"}
                    currency={drop.priceCode?.currency}
                  />
                </>
              )}
            </Grid.Bounds>

            {/* substitution notice */}

            <div className="quant-total">
              {!!lineMessage && <ProductUnavailableTag reason={lineMessage} />}

              <Grid.Box
                className={`quantity ${orderMode}`}
                valign="center"
                shiftRight
              >
                {orderMode === "edit" &&
              !orderLine.substituteOrderLine ? (
                <ProductQuantityControls
                  product={orderLine.product}
                  orderable={orderable}
                />
                  ) : (
                    <Text>
                      {orderLine.quantity}
                    </Text>
                  )}
              </Grid.Box>

              {drop && pricesVisible && (
                <Grid.Box
                  className="total"
                  shiftRight
                >
                  <Price
                    mode={orderMode}
                    product={orderLine.product ? orderLine.product : null}
                    installedModules={installedModules}
                    quantity={orderLine.quantity}
                    type="total"
                    line={orderLine}
                    currency={drop.priceCode?.currency}
                  />
                </Grid.Box>
              )}
            </div>

            {shouldShowOrderLineErrors(order, orderMode, drop.groupedSettings) &&
            !orderLine.substituteOrderLine && (
              <OrderLineError errors={orderLine.errors} />
            )}
          </>
        )}
      </OrderLineWrapper>

      {!!orderLine.substituteOrderLine && (
        <>
          <SubstitutionDivider>
            <Icon
              icon={Icons.SUBSTITUTION}
              $fillColor="midGrey"
              className="substitution-icon"
            />

            <Text weight="bold">
              Replaced by
            </Text>
          </SubstitutionDivider>

          <OrderLineWrapper
            direction="horizontal"
            valign="center"
            className="editable"
          >
            {order && drop && (
              <>
                <Grid.Box className="image-wrapper">
                  <BrandedImage
                    s3Key={orderLine.substituteOrderLine.product && orderLine.substituteOrderLine.product.image}
                    alt={orderLine.substituteOrderLine.product && orderLine.substituteOrderLine.product.description}
                    level="public"
                  />
                </Grid.Box>

                <Grid.Bounds
                  className="product-details"
                  direction="vertical"
                >
                  <div
                    onClick={() => orderLine.substituteOrderLine?.productId && setQuickViewProduct(orderLine.substituteOrderLine?.productId)}
                    className="link-text"
                  >
                    <TextTruncate
                      as="h3"
                      fontSize="h3"
                      truncateProps={{ lines: isMobile ? 1 : 2 }}
                    >
                      {orderLine.substituteOrderLine.product.description}
                    </TextTruncate>
                  </div>

                  <Text
                    color="secondary"
                    weight="bold"
                    textTransform="uppercase"
                    letterSpacing="big"
                    className="part-number"
                  >
                    {orderLine.substituteOrderLine.product.partNumber}
                  </Text>

                  {drop && pricesVisible && (
                    <>
                      <Price
                        mode={orderMode}
                        line={orderLine.substituteOrderLine}
                        product={orderLine.substituteOrderLine.product ? orderLine.substituteOrderLine.product : null}
                        installedModules={installedModules}
                        quantity={orderLine.substituteOrderLine.quantity}
                        type="unit"
                        currency={drop.priceCode?.currency}
                      />
                    </>
                  )}
                </Grid.Bounds>

                <div className="quant-total">
                  <Grid.Box
                    className={`quantity ${orderMode}`}
                    valign="center"
                    shiftRight
                  >
                    {orderMode === "edit" && canOrderProduct ? (
                      <ProductQuantity
                        canRemove={canRemove}
                        handleQuantityUpdate={newQuantity => dispatch(updateOrderLine(orderLine.product, newQuantity))}
                        initialQuantity={orderLine.substituteOrderLine.quantity}
                      />
                    ) : (
                      <Text>
                        {orderLine.substituteOrderLine.quantity}
                      </Text>
                    )}
                  </Grid.Box>

                  {drop && pricesVisible && (
                    <Grid.Box
                      className="total"
                      shiftRight
                    >
                      <Price
                        mode={orderMode}
                        product={orderLine.substituteOrderLine.product ? orderLine.substituteOrderLine.product : null}
                        installedModules={installedModules}
                        quantity={orderLine.substituteOrderLine.quantity}
                        type="total"
                        line={orderLine.substituteOrderLine}
                        currency={drop.priceCode?.currency}
                      />
                    </Grid.Box>
                  )}
                </div>

                {shouldShowOrderLineErrors(order, orderMode, drop.groupedSettings) && <OrderLineError errors={orderLine.substituteOrderLine.errors} />}
              </>
            )}
          </OrderLineWrapper>
        </>
      )}
    </>
  );
};

const OrderLineWrapper = styled(Grid.Bounds)<{ "$substitution"?: boolean; }>`
  ${({ theme, $substitution }) => css`
    background: ${theme.colors.white};
    margin-bottom: 0;
    padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.sm};
    min-height: 80px;
    justify-content: space-between;
    flex-direction: row;
    flex-wrap: wrap;
    padding-top: 50px;
    position: relative;
    border-top: 1px solid ${theme.colors.lightGrey};

    h3 {
      font-size: ${theme.fonts.sizes.h4};
    }

    .total-price,
    .current-price {
      font-size: calc(${theme.fonts.sizes.h4});
    }

    .image-wrapper {
      ${$substitution && css`
        opacity: 0.3;
      `}

      display: none;
      position: relative;
      background-color: ${theme.colors.lightGrey};
      width: 80px;
      height: 80px;
      overflow: hidden;
      flex-shrink: 0;
      > img {
        width: auto;
        height: auto;
        object-fit: contain;
        position: absolute;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
        max-height: 100%;
      }
      > img.placeholder {
        width: 30px;
      }
    }

    .product-details {
      align-self: flex-start;

      .part-number-wrapper {
        min-width: 110px;
      }

      .link-text {
        position: absolute;
        width: 100%;
        top: ${theme.spacing.padding.sm};
        left: 0;
        padding: 0 ${theme.spacing.padding.sm};
        cursor: pointer;
      }

      h3 {
        margin-bottom: 0px;
      }
    }

    .quant-total {
      display: flex;
      flex-direction: row;
      height: 100%;
      align-self: flex-start;
      align-items: center;
      justify-content: flex-end;
      width: 100%;
      flex-wrap: wrap;

      .quantity {
        padding-right: ${theme.spacing.padding.md};
        display: flex;
        align-items: center;
        margin: initial;
        order: 2;
        
        &.view span {
          font-size: ${theme.fonts.sizes.h4};
        }

        &.view span:before {
          content: "QTY";
          display: inline-block;
          margin-right: 5px;
          color: ${theme.fonts.colors.secondary};
        }
      }

      .total {
        padding-right: ${theme.spacing.padding.md};
        margin-right: ${theme.spacing.padding.md};
        margin-left: 0;
        order: 1;

        .current-price-wrapper {
          align-self: flex-start;

          .vat-wrapper {
            display: block;
            margin-left: 0;
          }

          .current-price {
            font-size: ${theme.fonts.sizes.headingScale};
          }
        }

        & > div {
          align-items: flex-end;
        }
      }

      .pack-size-selector-wrapper, .order-control-wrapper {
        display: flex;
        justify-content: flex-end;
      }
    }

    &.error {
      background: ${theme.colors.errorLight};
    }

    &.disabled {
      opacity: 0.5;

      .quant-total {
        .quantity {
          margin-top: 5px;
        }
      }
    }

    .quant-total {
      justify-content: space-between;
    }



    ${breakpoint("sm", theme)} {
      flex-direction: row;
    
      .product-details {
        min-width: 130px;
      }

      .quant-total {
        justify-content: flex-end;
        align-self: center;
        padding: 0;      
        
        .total {
          padding-right: 0;
          margin-top: 5px;

          .current-price-wrapper {
            align-self: initial;
          }
        }
      }
    }

    ${breakpoint("md", theme)} {
      justify-content: flex-start;
      padding-left: 0;
      padding: 0;
      height: auto;
      border-top: 0;
      margin-bottom: ${$substitution ? 0 : theme.spacing.margins.sm};
      
      .product-details {
        padding: ${theme.spacing.padding.sm} 0;

        .link-text {
          position: inherit;
          top: inherit;
          left: inherit;
          padding: 0;
        }
      }

      .image-wrapper {
        display: block;
        margin-right: ${theme.spacing.margins.sm};
      }

      .quant-total {

        .total {
          padding-right: ${theme.spacing.padding.sm};
        }
      }
    }

    ${breakpoint("lg", theme)} {
      padding: 0 ${theme.spacing.padding.sm};
      
      .quant-total {
        justify-content: flex-end;
      }
    }

    
    ${breakpoint("xxl", theme)} {
      justify-content: flex-start;
      padding-left: 0;
      padding-right: ${theme.spacing.padding.md};
      
      .image-wrapper {
        display: block;
        margin-right: ${theme.spacing.margins.md};

        width: 120px;
        height: 120px;
        > img.placeholder {
          width: 30px;
        }
      }

      .quant-total {
        flex-direction: row;
        flex-wrap: nowrap;
        min-width: 300px;
        width: 300px;
        margin-left: auto;

        .quantity {
          padding-right: 0;
        }
      }

      h3,
      .total-price,
      .current-price {
        font-size: ${theme.fonts.sizes.h4};
      }
    }

    ${breakpoint("xxl", theme)} {
      .product-details {
        padding-right: ${theme.spacing.padding.lg};
      }

      h3,
      .total-price,
      .current-price {
        font-size: ${theme.fonts.sizes.h4};
      }
    }



    ${$substitution && css`
      .product-details span,
      .quantity span,
      .current-price {
        color: ${theme.colors.ghost};
      }
      .total .current-price {
        text-decoration: line-through;
        text-decoration-color: ${theme.colors.midGrey};
      }
    `}
  `};
`;

const SubstitutionDivider = styled(Grid.Box)`
  ${({ theme }) => `
    background: ${theme.colors.lightGrey};
    padding: ${theme.spacing.padding.sm} 0;
    display: flex;
    flex-direction: row;
    align-items: center;

    .substitution-icon {
      margin-left: ${theme.spacing.padding.md}
    }

    ${breakpoint("md", theme)} {
      .substitution-icon {
        margin-left: 135px;
      }
    }


  
    span {
      margin-left: 15px;
      font-size: calc(${theme.fonts.sizes.p} * 0.8);
    }
  `}
`;

export default OrderProductLine;
